import type { CameraSpec, MountType } from '../services/cameraLayoutService';
import { DEFAULT_FOCAL_LENGTH_MM, DEFAULT_RESOLUTION_H, DEFAULT_RESOLUTION_W } from './cameraOptics';

/** 행 = 가로 줄(동일 y, x 방향으로 배치), 열 = 세로 칸(동일 x, y 방향 간격) — 천장·벽 공통 */
export const DEFAULT_CEILING_GRID_ROWS = 2;
export const DEFAULT_CEILING_GRID_COLS = 2;
export const DEFAULT_WALL_GRID_ROWS = 1;
export const DEFAULT_WALL_GRID_COLS = 2;
export const DEFAULT_WALL_POOL_EDGE_OFFSET_M = 1.0;
export const DEFAULT_WALL_MOUNT_OFFSET_M = 0.2;
/** 벽 CCTV 자동 배치 시 하향각 기본값 */
export const DEFAULT_WALL_TILT_DEG = 20;

/** 도면 기준: 상=윗변(장변), 하=아랫변, 좌·우=단변 */
export type WallSide = 'top' | 'bottom' | 'left' | 'right';

export type WallPoolEdgeOffsetsM = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

export const DEFAULT_WALL_POOL_EDGE_OFFSETS_M: WallPoolEdgeOffsetsM = {
  top: DEFAULT_WALL_POOL_EDGE_OFFSET_M,
  bottom: DEFAULT_WALL_POOL_EDGE_OFFSET_M,
  left: DEFAULT_WALL_POOL_EDGE_OFFSET_M,
  right: DEFAULT_WALL_POOL_EDGE_OFFSET_M,
};

export function uniformWallPoolEdgeOffsets(m: number): WallPoolEdgeOffsetsM {
  const v = Math.max(0, Number(m) || 0);
  return { top: v, bottom: v, left: v, right: v };
}

export function clampWallPoolEdgeOffsets(
  o: Partial<WallPoolEdgeOffsetsM> | null | undefined,
  fallbackM = DEFAULT_WALL_POOL_EDGE_OFFSET_M,
): WallPoolEdgeOffsetsM {
  const fb = Math.max(0, Number(fallbackM) || DEFAULT_WALL_POOL_EDGE_OFFSET_M);
  const n = (v: unknown) => {
    const x = Number(v);
    return Number.isFinite(x) && x >= 0 ? x : fb;
  };
  return {
    top: n(o?.top),
    bottom: n(o?.bottom),
    left: n(o?.left),
    right: n(o?.right),
  };
}

/** 격자 place 문자열 → 벽 방향 */
export function wallSideFromPlaceLabel(place: string): WallSide | null {
  const s = String(place || '');
  if (s.includes('윗')) return 'top';
  if (s.includes('아랫')) return 'bottom';
  if (s.includes('왼쪽')) return 'left';
  if (s.includes('오른쪽')) return 'right';
  return null;
}

/** 도면 heading: 0=+x(우측), 90=+y(아래), 180=좌, 270=위 */
export function normalizeHeadingDeg(deg: number): number {
  const n = Number(deg);
  if (!Number.isFinite(n)) return 0;
  return ((n % 360) + 360) % 360;
}

/**
 * 그 벽에서 「수면 안쪽 정면」(벽면 수직)의 도면 heading.
 * 윗벽=90(아래), 아랫벽=270(위), 좌=0(우), 우=180(좌).
 */
export function wallFaceHeadingDeg(side: WallSide): number {
  if (side === 'top') return 90;
  if (side === 'bottom') return 270;
  if (side === 'left') return 0;
  return 180;
}

/**
 * 개별 카메라 기준 오프셋(° , -180~180).
 * 0 = 정면(벽⊥·수면 안쪽). 양수 = 카메라 우측, 음수 = 좌측.
 * 저장·시야 계산은 도면 절대 heading을 유지.
 */
export function absoluteToCameraRelativePanDeg(
  absoluteDeg: number,
  faceDeg: number,
): number {
  let r = normalizeHeadingDeg(absoluteDeg - faceDeg);
  if (r > 180) r -= 360;
  return Math.round(r * 10) / 10;
}

export function cameraRelativeToAbsolutePanDeg(
  relativeDeg: number,
  faceDeg: number,
): number {
  return Math.round(normalizeHeadingDeg(faceDeg + Number(relativeDeg)) * 10) / 10;
}

/**
 * UI·도면 화살표 표시각: 0=정면(수면 안쪽), +우측, −좌측.
 * 윗·아랫·좌·우 벽캠 동일. (저장값은 도면 절대 heading)
 */
export function absoluteToCameraPanDisplayDeg(
  absoluteDeg: number,
  faceDeg: number,
): number {
  return absoluteToCameraRelativePanDeg(absoluteDeg, faceDeg);
}

export function cameraPanDisplayToAbsoluteDeg(
  displayDeg: number,
  faceDeg: number,
): number {
  return cameraRelativeToAbsolutePanDeg(Number(displayDeg), faceDeg);
}

/** 벽캠 여부 — mount_type 또는 이름(「벽 …」) */
export function isWallCameraSpec(cam: {
  mount_type?: string;
  name?: string | null;
}): boolean {
  if (cam.mount_type === 'wall') return true;
  const n = String(cam.name || '');
  return n.includes('벽') && !n.includes('천장');
}

/** 카메라 이름·좌표로 가장 가까운 벽 방향 추정 */
export function inferWallSide(
  cam: { x?: number; y?: number; name?: string },
  bounds?: PoolBounds,
): WallSide {
  const fromName = wallSideFromPlaceLabel(String(cam.name || ''));
  if (fromName) return fromName;
  const b = bounds ?? { minX: 0, maxX: 100, minY: 0, maxY: 100 };
  const x = Number(cam.x);
  const y = Number(cam.y);
  const xx = Number.isFinite(x) ? x : (b.minX + b.maxX) / 2;
  const yy = Number.isFinite(y) ? y : (b.minY + b.maxY) / 2;
  const dTop = Math.abs(yy - b.minY);
  const dBottom = Math.abs(b.maxY - yy);
  const dLeft = Math.abs(xx - b.minX) + 1e-3;
  const dRight = Math.abs(b.maxX - xx) + 1e-3;
  const m = Math.min(dTop, dBottom, dLeft, dRight);
  if (m === dTop) return 'top';
  if (m === dBottom) return 'bottom';
  if (m === dLeft) return 'left';
  return 'right';
}

/** 수면 왼쪽 끝(상·하 벽) 또는 위쪽 끝(좌·우 벽)에서 카메라까지, m. 도면 치수와 같은 기준. */
export function alongEdgeMeters(
  cam: { x?: number; y?: number },
  side: WallSide,
  bounds: PoolBounds,
  poolLengthM: number,
  poolWidthM: number,
): number {
  const spanX = Math.max(1e-6, bounds.maxX - bounds.minX);
  const spanY = Math.max(1e-6, bounds.maxY - bounds.minY);
  const x = Number(cam.x);
  const y = Number(cam.y);
  if (side === 'left' || side === 'right') {
    const yy = Number.isFinite(y) ? y : bounds.minY;
    return ((yy - bounds.minY) / spanY) * Math.max(poolWidthM, 0.01);
  }
  const xx = Number.isFinite(x) ? x : bounds.minX;
  return ((xx - bounds.minX) / spanX) * Math.max(poolLengthM, 0.01);
}

/** 긴 변·짧은 변을 따라 거리(m)만 바꾸고, 벽에서 떨어진 축은 유지. */
export function normFromAlongEdgeMeters(
  cam: { x?: number; y?: number },
  side: WallSide,
  meters: number,
  bounds: PoolBounds,
  poolLengthM: number,
  poolWidthM: number,
): { x: number; y: number } {
  const spanX = Math.max(1e-6, bounds.maxX - bounds.minX);
  const spanY = Math.max(1e-6, bounds.maxY - bounds.minY);
  const x0 = Number(cam.x);
  const y0 = Number(cam.y);
  const round3 = (v: number) => Math.round(v * 1000) / 1000;
  if (side === 'left' || side === 'right') {
    const spanM = Math.max(poolWidthM, 0.01);
    const m = Math.max(0, Math.min(spanM, Number(meters) || 0));
    return {
      x: Number.isFinite(x0) ? x0 : side === 'left' ? bounds.minX : bounds.maxX,
      y: round3(bounds.minY + (m / spanM) * spanY),
    };
  }
  const spanM = Math.max(poolLengthM, 0.01);
  const m = Math.max(0, Math.min(spanM, Number(meters) || 0));
  return {
    x: round3(bounds.minX + (m / spanM) * spanX),
    y: Number.isFinite(y0) ? y0 : side === 'bottom' ? bounds.maxY : bounds.minY,
  };
}

export function wallPoolEdgeForSide(
  offsets: WallPoolEdgeOffsetsM,
  side: WallSide,
): number {
  return Math.max(0, Number(offsets[side]) || 0);
}

/**
 * 카메라 광학 중심 ↔ 풀 가장자리 거리(m).
 * 벽·풀 간격에서 벽→카메라 돌출(풀 쪽)을 뺀 값. 예: 1.0 − 0.2 = 0.8
 */
export function wallCameraToPoolGapM(
  poolEdgeOffsetM: number,
  wallMountOffsetM: number,
): number {
  return Math.max(
    0,
    Math.max(0, poolEdgeOffsetM) - Math.max(0, wallMountOffsetM),
  );
}

/**
 * 벽캠 광학 중심(m). 풀 가장자리 좌표에서 벽면 **수직**으로만 바깥 이동.
 * (풀 중심 방사형 이동은 모서리에서 x가 크게 틀어져 2×3 배치 FOV가 이상해짐)
 */
export function wallOpticalCenterMeters(
  mapXM: number,
  mapYM: number,
  poolLengthM: number,
  poolWidthM: number,
  poolEdgeOffsetM: number,
  wallMountOffsetM: number,
  sideHint?: WallSide | null,
): { x: number; y: number } {
  const shift = wallCameraToPoolGapM(poolEdgeOffsetM, wallMountOffsetM);
  if (shift <= 1e-9) return { x: mapXM, y: mapYM };
  const L = Math.max(poolLengthM, 0.01);
  const W = Math.max(poolWidthM, 0.01);
  let side = sideHint ?? null;
  if (!side) {
    const dTop = Math.max(0, mapYM);
    const dBot = Math.max(0, W - mapYM);
    const dLeft = Math.max(0, mapXM) + 1e-3;
    const dRight = Math.max(0, L - mapXM) + 1e-3;
    const m = Math.min(dTop, dBot, dLeft, dRight);
    if (m === dTop) side = 'top';
    else if (m === dBot) side = 'bottom';
    else if (m === dLeft) side = 'left';
    else side = 'right';
  }
  if (side === 'top') return { x: mapXM, y: mapYM - shift };
  if (side === 'bottom') return { x: mapXM, y: mapYM + shift };
  if (side === 'left') return { x: mapXM - shift, y: mapYM };
  return { x: mapXM + shift, y: mapYM };
}

export type PoolBounds = { minX: number; maxX: number; minY: number; maxY: number };

export function poolBoundsFromPolygon(poly: number[][]): PoolBounds {
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

function headingTowardCenter(x: number, y: number, cx: number, cy: number): number {
  const deg = (Math.atan2(cy - y, cx - x) * 180) / Math.PI;
  return Math.round(((deg % 360) + 360) % 360);
}

function lerpAlong(i: number, n: number, a: number, b: number): number {
  if (n <= 0) return (a + b) / 2;
  return a + ((i + 0.5) / n) * (b - a);
}

function round1(v: number) {
  return Math.round(v * 10) / 10;
}

/** 도면·보고서 공통: 거리(m) 소수 1자리 */
export function roundMeters1(v: number) {
  return round1(v);
}

/** 도면 치수 라벨 (예: "3.5 m") — 보고서와 동일 반올림 */
export function formatMeters1(v: number): string {
  return `${round1(v)} m`;
}

type WallCellPlacement = { x: number; y: number; place: string };

/**
 * 벽 격자 (행×열) 한 셀의 도면 좌표.
 * - 1행: 윗쪽 벽, 마지막 행: 아랫쪽 벽 (열 = 가로 위치)
 * - 중간 행: 1열=왼쪽 벽, 마지막 열=오른쪽 벽 (행 간 y 분배)
 * - 중간 행·중간 열: 시설 안쪽(가운데)
 * - 중간 행이 1줄뿐이고 열≥3이면 예시(3×3)처럼 1열=오른쪽 벽, 마지막 열=가운데·우측
 */
export function wallGridCellPlacement(
  row: number,
  col: number,
  rows: number,
  cols: number,
  b: PoolBounds,
): WallCellPlacement {
  const numMiddleRows = Math.max(0, rows - 2);
  const isTopRow = row === 0;
  const isBottomRow = row === rows - 1;
  const isFirstCol = col === 0;
  const isLastCol = col === cols - 1;
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;

  if (rows === 1) {
    return {
      x: lerpAlong(col, cols, b.minX, b.maxX),
      y: b.minY,
      place: '윗쪽벽',
    };
  }

  if (cols === 1) {
    if (isTopRow) {
      return { x: cx, y: b.minY, place: '윗쪽벽' };
    }
    if (isBottomRow) {
      return { x: cx, y: b.maxY, place: '아랫쪽벽' };
    }
    const mi = row - 1;
    return {
      x: b.minX,
      y: lerpAlong(mi, numMiddleRows, b.minY, b.maxY),
      place: '왼쪽벽',
    };
  }

  if (isTopRow) {
    return {
      x: lerpAlong(col, cols, b.minX, b.maxX),
      y: b.minY,
      place: '윗쪽벽',
    };
  }

  if (isBottomRow) {
    return {
      x: lerpAlong(col, cols, b.minX, b.maxX),
      y: b.maxY,
      place: '아랫쪽벽',
    };
  }

  const mi = row - 1;
  const ySide = lerpAlong(mi, numMiddleRows, b.minY, b.maxY);

  if (isFirstCol) {
    if (numMiddleRows === 1 && cols >= 3) {
      return { x: b.maxX, y: cy, place: '오른쪽벽' };
    }
    return { x: b.minX, y: ySide, place: '왼쪽벽' };
  }

  if (isLastCol) {
    if (numMiddleRows === 1 && cols >= 3) {
      const span = (b.maxX - b.minX) * 0.25;
      return { x: cx + span, y: cy, place: '가운데·우측' };
    }
    return { x: b.maxX, y: ySide, place: '오른쪽벽' };
  }

  const innerIndex = col - 1;
  const innerCount = cols - 2;
  return {
    x: lerpAlong(col, cols, b.minX, b.maxX),
    y: lerpAlong(row, rows, b.minY, b.maxY),
    place: innerCount === 1 ? '가운데' : `가운데-${innerIndex + 1}`,
  };
}

export type GridPlacementOptions = {
  bounds: PoolBounds;
  /** 천장 행: 가로 줄 수 (y 방향) */
  ceilingRows: number;
  /** 천장 열: 세로 칸 수 (x 방향) */
  ceilingCols: number;
  /**
   * 벽 행×열: 긴 변(윗·아랫) 가로 격자.
   * 0이면 윗·아랫 벽캠 없음(좌·우만 쓸 수 있음).
   */
  wallRows: number;
  wallCols: number;
  /** 왼쪽 짧은 변에 둘 벽캠 대수 (0=없음) */
  wallLeftCount?: number;
  /** 오른쪽 짧은 변에 둘 벽캠 대수 (0=없음) */
  wallRightCount?: number;
  /** @deprecated wallPoolEdgeOffsetsM 사용 — 있으면 4면 동일값으로 취급 */
  wallPoolEdgeOffsetM?: number;
  /** 상·하·좌·우 벽↔풀 간격(m). 없으면 wallPoolEdgeOffsetM 또는 기본 1.0 */
  wallPoolEdgeOffsetsM?: Partial<WallPoolEdgeOffsetsM>;
  wallMountOffsetM: number;
  defaultFocalLengthMm?: number;
};

/** 좌·우 벽면: 폭 방향으로 등분 배치 (모서리는 살짝 안쪽) */
function placeSideWallCameras(
  side: 'left' | 'right',
  count: number,
  b: PoolBounds,
  wallBase: Partial<CameraSpec>,
  cx: number,
  cy: number,
): Partial<CameraSpec>[] {
  const n = Math.max(0, Math.min(12, Math.floor(count)));
  if (n <= 0) return [];
  const spanY = Math.max(1e-6, b.maxY - b.minY);
  const inset = Math.min(spanY * 0.08, spanY * 0.35);
  const y0 = b.minY + inset;
  const y1 = b.maxY - inset;
  const x = side === 'left' ? b.minX : b.maxX;
  const place = side === 'left' ? '왼쪽벽' : '오른쪽벽';
  const out: Partial<CameraSpec>[] = [];
  for (let i = 0; i < n; i++) {
    const y = n === 1 ? (y0 + y1) / 2 : y0 + ((i + 0.5) / n) * (y1 - y0);
    out.push({
      ...wallBase,
      x: round1(x),
      y: round1(y),
      heading_deg: headingTowardCenter(x, y, cx, cy),
      name: `벽 ${place} ${i + 1}/${n}`,
    });
  }
  return out;
}

/** 천장=내부 격자 중앙, 벽=행·열(윗·아랫) + 좌·우 대수 */
export function buildCamerasFromGrid(opts: GridPlacementOptions): Partial<CameraSpec>[] {
  const {
    bounds: b,
    ceilingRows,
    ceilingCols,
    wallRows,
    wallCols,
    wallLeftCount = 0,
    wallRightCount = 0,
    wallPoolEdgeOffsetM,
    wallPoolEdgeOffsetsM,
    wallMountOffsetM,
  } = opts;
  const defaultFocal = opts.defaultFocalLengthMm ?? DEFAULT_FOCAL_LENGTH_MM;
  const edgeOffsets = clampWallPoolEdgeOffsets(
    wallPoolEdgeOffsetsM,
    wallPoolEdgeOffsetM ?? DEFAULT_WALL_POOL_EDGE_OFFSET_M,
  );
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const cams: Partial<CameraSpec>[] = [];

  const rows = Math.max(0, Math.min(12, Math.floor(ceilingRows)));
  const cols = Math.max(0, Math.min(12, Math.floor(ceilingCols)));
  if (rows > 0 && cols > 0) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = lerpAlong(col, cols, b.minX, b.maxX);
        const y = lerpAlong(row, rows, b.minY, b.maxY);
        cams.push({
          mount_type: 'ceiling',
          x: round1(x),
          y: round1(y),
          heading_deg: 90,
          tilt_deg: 90,
          name: `천장 ${row + 1}행-${col + 1}열`,
          resolution_w: DEFAULT_RESOLUTION_W,
          resolution_h: DEFAULT_RESOLUTION_H,
          focal_length_mm: defaultFocal,
        });
      }
    }
  }

  const wRows = Math.max(0, Math.min(12, Math.floor(wallRows)));
  const wCols = Math.max(0, Math.min(12, Math.floor(wallCols)));
  const wallBaseCommon = {
    mount_type: 'wall' as MountType,
    wall_mount_offset_m: wallMountOffsetM,
    resolution_w: DEFAULT_RESOLUTION_W,
    resolution_h: DEFAULT_RESOLUTION_H,
    focal_length_mm: defaultFocal,
    tilt_deg: DEFAULT_WALL_TILT_DEG,
  };

  // b) 가로(윗·아랫) 행×열 — 기존과 동일
  if (wRows > 0 && wCols > 0) {
    for (let row = 0; row < wRows; row++) {
      for (let col = 0; col < wCols; col++) {
        const { x, y, place } = wallGridCellPlacement(row, col, wRows, wCols, b);
        const side = wallSideFromPlaceLabel(place) || inferWallSide({ x, y, name: place }, b);
        cams.push({
          ...wallBaseCommon,
          pool_edge_offset_m: wallPoolEdgeForSide(edgeOffsets, side),
          x: round1(x),
          y: round1(y),
          heading_deg: headingTowardCenter(x, y, cx, cy),
          name: `벽 ${row + 1}행 ${col + 1}열 (${place})`,
        });
      }
    }
  }

  // a/c) 좌·우 벽 대수 (행·열과 독립)
  cams.push(
    ...placeSideWallCameras(
      'left',
      wallLeftCount,
      b,
      {
        ...wallBaseCommon,
        pool_edge_offset_m: wallPoolEdgeForSide(edgeOffsets, 'left'),
      },
      cx,
      cy,
    ),
    ...placeSideWallCameras(
      'right',
      wallRightCount,
      b,
      {
        ...wallBaseCommon,
        pool_edge_offset_m: wallPoolEdgeForSide(edgeOffsets, 'right'),
      },
      cx,
      cy,
    ),
  );

  return cams;
}
